import { TopFiveVendors } from "components/BooksHomePage/BusinessInsights/BusinessInsights";
import { Button } from "components/DesignSystem/Button/Button";
import { CATEGORISATION_TYPE, INSIGHT_TYPE } from "constants/booksHomePage";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { Link } from "react-router-dom";
import { useGetInsightsQuery } from "store/apis/metrics";

type TopInsightCardProps = {
  startDate: string;
  endDate: string;
  insightType: keyof typeof INSIGHT_TYPE;
  title: string;
  viewAllUrl: string;
  viewAllText: string;
  groupId?: string;
  isExport?: boolean;
};

const TopInsightCard = ({
  startDate,
  endDate,
  insightType,
  title,
  viewAllUrl,
  viewAllText,
  groupId: groupIdProp,
  isExport = false,
}: TopInsightCardProps) => {
  const { uuid: groupIdFromContext } = useCurrentGroupContext();
  const groupId = groupIdProp ?? groupIdFromContext;
  const entityId = useCurrentEntityId();

  const { data, isLoading, isSuccess, isFetching, isError } =
    useGetInsightsQuery(
      {
        groupId,
        entityId,
        start_date: startDate,
        end_date: endDate,
        limit: 5,
        insight_type: insightType,
        categorisation_type: CATEGORISATION_TYPE.ALL,
      },
      { skip: !groupId || !entityId }
    );

  const merchants = data?.merchants ?? [];
  const isRevenue = insightType === INSIGHT_TYPE.REVENUE;
  const columnHeader = isRevenue ? "Customers" : "Merchant";
  const amountHeader = isRevenue ? "Income" : "Expense";

  return (
    <div className="t-flex t-flex-col t-gap-4 t-bg-surface t-border t-border-solid t-border-neutral-10 t-rounded-lg t-p-5 sm:t-px-3 t-h-full">
      <div className="t-pb-4 t-border-b t-border-solid t-border-neutral-10 t-border-x-0 t-border-t-0">
        <span className="t-text-subtitle-lg t-text-text-black">{title}</span>
      </div>
      {isSuccess && merchants.length > 0 && (
        <div className="t-flex t-justify-between t-py-2">
          <span className="t-text-body-sm-lite t-text-text-30">
            {columnHeader}
          </span>
          <span className="t-text-body-sm-lite t-text-text-30">
            {amountHeader}
          </span>
        </div>
      )}
      <div className="t-flex t-flex-col t-flex-1">
        <div className="t-flex-1">
          <TopFiveVendors
            top_five_merchants={merchants}
            isInsightsLoading={isLoading}
            isInsightsFetching={isFetching}
            isInsightsSuccess={isSuccess}
            isInsightsError={isError}
            insightType={insightType}
          />
        </div>
        {isSuccess && merchants.length > 0 && !isExport && (
          <Link to={viewAllUrl} className="t-mt-auto">
            <Button size="xsmall" customType="link">
              <span>{viewAllText}</span>
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

type InsightCardProps = {
  startDate: string;
  endDate: string;
  groupId?: string;
  isExport?: boolean;
};

export const TopCustomers = ({
  startDate,
  endDate,
  groupId,
  isExport,
}: InsightCardProps) => {
  const { link } = useConstructInternalLink();

  return (
    <TopInsightCard
      startDate={startDate}
      endDate={endDate}
      groupId={groupId}
      insightType={INSIGHT_TYPE.REVENUE}
      title="Top Customers"
      viewAllUrl={link("/books/revenue/customers")}
      viewAllText="View all Customers"
      isExport={isExport}
    />
  );
};

export const TopVendors = ({
  startDate,
  endDate,
  groupId,
  isExport,
}: InsightCardProps) => {
  const { link } = useConstructInternalLink();

  return (
    <TopInsightCard
      startDate={startDate}
      endDate={endDate}
      groupId={groupId}
      insightType={INSIGHT_TYPE.EXPENSE}
      title="Top Vendors"
      viewAllUrl={link("/books/expenses/vendors")}
      viewAllText="View all Vendors"
      isExport={isExport}
    />
  );
};
